import classNames from "classnames";
import React, { ReactNode } from "react";
import ReactDOM from "react-dom";
import { Icon } from "../icon/icon";
import { Button } from "../buttons/button";

type ModalProps = {
  open: boolean;
  handleClose: () => void;
  children: ReactNode;
  footerChildren?: ReactNode;
  className?: string;
  dialogClassName?: string;
  containerClassName?: string;
  showIcon?: boolean;
  iconType?: string;
  iconHeight?: number;
  iconWidth?: number;
  showBackIcon?: boolean;
  showBackButton?: boolean;
  showCloseIcon?: boolean;
  closeButtonClassName?: string;
  handleBack?: () => void;
  title?: string;
  subtitle?: string;
  closeOnOverlayClick?: boolean;
  showHeader?: boolean;
  fullScreen?: boolean;
  mobileAnchorBottom?: boolean;
  showMobileTopBar?: boolean;
};

const ModalComponent: React.FC<ModalProps> = ({
  open,
  handleClose,
  children,
  footerChildren,
  className,
  dialogClassName = "",
  containerClassName = "",
  showBackIcon = true,
  showBackButton = false,
  showIcon = false,
  iconType = "",
  iconHeight = 20,
  iconWidth = 20,
  showCloseIcon = true,
  closeButtonClassName = "",
  handleBack = () => {},
  title = "",
  subtitle = "",
  closeOnOverlayClick = false,
  showHeader = true,
  fullScreen = false,
  mobileAnchorBottom = false,
  showMobileTopBar = true,
}) => {
  const closeModal = () => {
    handleClose();
  };

  return open
    ? ReactDOM.createPortal(
        <>
          <div
            className={classNames(
              `fixed inset-0 !z-[999] w-screen overflow-y-scroll py-[20px]`,
              "flex h-screen max-h-[100dvh] justify-center md:items-center",
              `${mobileAnchorBottom ? "items-end !py-0" : "items-center px-[20px]"}`,
              containerClassName,
            )}
          >
            {/* Overlay  */}
            <div
              className="absolute inset-0 z-10 max-h-full bg-black/40"
              onClick={() => {
                if (closeOnOverlayClick) {
                  closeModal();
                }
              }}
            />

            {/* Modal dialog  */}
            <div
              className={`z-30 w-full bg-white p-6 ${!fullScreen && "md:max-w-[580px]"} ${!fullScreen ? "!h-fit max-h-[90dvh]" : "h-full"} ${!fullScreen && `${mobileAnchorBottom ? "rounded-t-lg !p-5 md:!p-6" : "rounded-2xl"} md:rounded-2xl`} ani-rise flex flex-col overflow-hidden ${className}`}
            >
              {mobileAnchorBottom && showMobileTopBar ? (
                <div className="mb-4 flex w-full items-center justify-center md:hidden">
                  <hr className="h-1 w-8 rounded-xl bg-grey-400" />
                </div>
              ) : undefined}

              {/* Header  */}
              {showBackButton && (
                <div className="mb-3">
                  <Button
                    text="Back"
                    icon="arrow-left"
                    type="button"
                    size="smaller"
                    onClick={handleBack}
                    variation="plain-grey-3"
                    iconClassName={"mb-1"}
                    className="max-w-fit"
                  />
                </div>
              )}

              {showIcon && (
                <div className="mb-2 w-fit rounded-xl border border-gray-100 px-2 py-1.5">
                  <Icon
                    svg={iconType}
                    className="soft-shrink mb-1 text-grey-700"
                    width={iconWidth}
                    height={iconHeight}
                  />
                </div>
              )}

              {showHeader && (
                <div className={`flex items-center justify-between gap-4`}>
                  <div className="flex items-center gap-4">
                    {showBackIcon && (
                      <Icon
                        svg={"left-caret"}
                        onClick={handleBack}
                        className="soft-shrink"
                        width={14}
                        height={14}
                      />
                    )}

                    {title && (
                      <div>
                        <p className="text-lg font-bold md:text-base">
                          {title}
                        </p>
                        <p className="text-sm text-grey-500">{subtitle}</p>
                      </div>
                    )}
                  </div>

                  {showCloseIcon && (
                    <div className="flex flex-1 items-end justify-end">
                      <button
                        type="button"
                        onClick={closeModal}
                        className={`w-fit rounded-full bg-grey-50 p-[6px] ${closeButtonClassName}`}
                      >
                        <Icon
                          svg={"close-icon"}
                          className="soft-shrink"
                          width={24}
                          height={24}
                        />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Dialog  */}
              <div
                className={`hide-scroll-bar my-4 flex-1 overflow-y-scroll ${dialogClassName} `}
              >
                {children}
              </div>

              {/* Footer  */}
              {footerChildren && (
                <div className={`flex items-center justify-end gap-2`}>
                  {footerChildren}
                </div>
              )}
            </div>
          </div>
        </>,
        document.body,
      )
    : null;
};

export default ModalComponent;
